import { doLog } from "@/lib/globals";
import { postError } from "./api_controller";

class RequestQueue {
  constructor(options = {}) {
    this.queue = [];
    this.processing = false;
    this.maxRetries = options.maxRetries || 3;
    this.baseDelay = options.baseDelay || 1000;
    this.maxDelay = options.maxDelay || 30000;
    this.onRequestFailed = options.onRequestFailed || null;
    this.maxQueueSize = options.maxQueueSize || 100;
    this.isOnline = navigator.onLine;
    this.visibilityState = document.visibilityState;
    this.requestTimeout = options.requestTimeout || 30000; // 30 seconds default timeout
    this.maxQueueTime = options.maxQueueTime || 300000; // 5 minutes default max queue time

    // Circuit breaker state
    this.circuitBreaker = {
      failures: 0,
      lastFailureTime: null,
      state: "CLOSED", // CLOSED, OPEN, HALF_OPEN
      threshold: 5, // Number of failures before opening circuit
      resetTimeout: 30000, // Time to wait before attempting to close circuit
    };

    // Initialize analytics first
    this.analytics = {
      totalRequests: 0,
      successfulRequests: 0,
      failedRequests: 0,
      retries: 0,
      requestTypes: new Map(),
      processingTimes: [],
      errors: new Map(),
      timeouts: 0,
      circuitBreakerTrips: 0,
      requestTypeStats: new Map(),
      hourlyStats: new Map(),
      recoveryAttempts: 0,
      successfulRecoveries: 0,
    };

    // Then load persisted state
    this.loadAnalytics();

    // Setup periodic state persistence
    this.setupAnalyticsPersistence();

    // Enhanced timeout configuration
    this.timeoutConfig = {
      // Base timeouts by network type (in milliseconds)
      networkTimeouts: {
        wifi: 10000, // 10 seconds for WiFi
        "5g": 15000, // 15 seconds for 5G
        "4g": 20000, // 20 seconds for 4G
        "3g": 30000, // 30 seconds for 3G
        "2g": 45000, // 45 seconds for 2G
        "slow-2g": 60000, // 60 seconds for slow 2G
        unknown: 30000, // Default 30 seconds for unknown
      },

      // Additional timeout factors
      factors: {
        // RTT multiplier (add extra time based on round-trip time)
        rttMultiplier: 2,

        // Bandwidth thresholds (Mbps)
        bandwidthThresholds: {
          high: 10, // High bandwidth (5G, good WiFi)
          medium: 5, // Medium bandwidth (4G, average WiFi)
          low: 2, // Low bandwidth (3G, poor WiFi)
        },

        // Bandwidth multipliers
        bandwidthMultipliers: {
          high: 1, // No additional time for high bandwidth
          medium: 1.5, // 50% more time for medium bandwidth
          low: 2, // Double time for low bandwidth
        },
      },
    };

    // Initialize network info with fallback
    this.networkInfo = navigator.connection || {
      type: "unknown",
      effectiveType: "unknown",
      rtt: 100,
      downlink: 1,
    };

    // Setup event listeners
    this.setupEventListeners();
  }

  // MARK: setupAnalyticsPersistence
  setupAnalyticsPersistence() {
    // Save analytics every 30 seconds
    setInterval(() => {
      try {
        this.persistAnalytics();
      } catch (error) {
        console.error("Error in periodic analytics persistence:", error);
      }
    }, 30000);
  }

  // MARK: persistAnalytics
  persistAnalytics() {
    try {
      // Convert all Maps to plain objects, including nested ones
      const serializedAnalytics = {
        totalRequests: this.analytics.totalRequests,
        successfulRequests: this.analytics.successfulRequests,
        failedRequests: this.analytics.failedRequests,
        retries: this.analytics.retries,
        timeouts: this.analytics.timeouts,
        circuitBreakerTrips: this.analytics.circuitBreakerTrips,
        recoveryAttempts: this.analytics.recoveryAttempts,
        successfulRecoveries: this.analytics.successfulRecoveries,
        requestTypes: Object.fromEntries(this.analytics.requestTypes),
        errors: Object.fromEntries(this.analytics.errors),
        requestTypeStats: Object.fromEntries(
          Array.from(this.analytics.requestTypeStats.entries()).map(([key, value]) => [
            key,
            {
              ...value,
              errors: Object.fromEntries(value.errors),
            },
          ])
        ),
        hourlyStats: Object.fromEntries(this.analytics.hourlyStats),
        // Only persist the last 100 processing times
        processingTimes: this.analytics.processingTimes.slice(-100),
      };

      const state = {
        analytics: serializedAnalytics,
      };
      localStorage.setItem("taalia_queue_state", JSON.stringify(state));
    } catch (error) {
      console.error("Error persisting analytics:", error);
    }
  }

  // MARK: loadAnalytics
  loadAnalytics() {
    try {
      const state = localStorage.getItem("taalia_queue_state");
      if (state) {
        const { analytics } = JSON.parse(state);

        // Merge with existing analytics
        this.analytics = {
          totalRequests:
            (this.analytics.totalRequests || 0) + (analytics.totalRequests || 0),
          successfulRequests:
            (this.analytics.successfulRequests || 0) +
            (analytics.successfulRequests || 0),
          failedRequests:
            (this.analytics.failedRequests || 0) + (analytics.failedRequests || 0),
          retries: (this.analytics.retries || 0) + (analytics.retries || 0),
          timeouts: (this.analytics.timeouts || 0) + (analytics.timeouts || 0),
          circuitBreakerTrips:
            (this.analytics.circuitBreakerTrips || 0) +
            (analytics.circuitBreakerTrips || 0),
          recoveryAttempts:
            (this.analytics.recoveryAttempts || 0) + (analytics.recoveryAttempts || 0),
          successfulRecoveries:
            (this.analytics.successfulRecoveries || 0) +
            (analytics.successfulRecoveries || 0),
          requestTypes: new Map(Object.entries(analytics.requestTypes || {})),
          errors: new Map(Object.entries(analytics.errors || {})),
          requestTypeStats: new Map(
            Object.entries(analytics.requestTypeStats || {}).map(([key, value]) => [
              key,
              {
                ...value,
                errors: new Map(Object.entries(value.errors || {})),
              },
            ])
          ),
          hourlyStats: new Map(Object.entries(analytics.hourlyStats || {})),
          processingTimes: [...(analytics.processingTimes || [])],
        };
      }
    } catch (error) {
      console.error("Error loading analytics:", error);
    }
  }

  // MARK: setupEventListeners
  setupEventListeners() {
    // Handle online/offline events
    window.addEventListener("online", () => {
      this.isOnline = true;
      this.process(); // Process queue when back online
    });
    window.addEventListener("offline", () => {
      this.isOnline = false;
    });

    // Handle visibility change
    document.addEventListener("visibilitychange", () => {
      this.visibilityState = document.visibilityState;
      if (this.visibilityState === "visible") {
        // Process queue when becoming visible
        this.process();
      }
    });

    // Handle beforeunload - persist analytics
    window.addEventListener("beforeunload", () => {
      this.persistAnalytics();
    });

    // Handle network changes
    if (navigator.connection) {
      navigator.connection.addEventListener("change", () => {
        this.updateNetworkInfo();
        this.recalculateTimeouts();

        // If we're back online, process the queue
        if (this.isOnline) {
          this.process();
        }

        // Log network change
        if (doLog()) {
          console.log("Network changed:", {
            type: this.networkInfo.type,
            effectiveType: this.networkInfo.effectiveType,
            rtt: this.networkInfo.rtt,
            downlink: this.networkInfo.downlink,
          });
        }
      });
    }
  }

  // MARK: calculateBackoff
  calculateBackoff(retryCount) {
    const delay = Math.min(
      this.maxDelay,
      this.baseDelay * Math.pow(2, retryCount) + Math.random() * 1000
    );
    return delay;
  }

  // MARK: getRequestKey
  getRequestKey(request) {
    // Create a unique key for the request based on its properties
    const { url, options } = request;
    // eslint-disable-next-line no-unused-vars
    const { method, body, headers } = options;

    // Include relevant headers in the key to better identify unique requests
    const relevantHeaders = {
      //   "Event-Name": headers["Event-Name"],
      //   "Content-Type": headers["Content-Type"],
      // Add other headers that make a request unique
    };

    return `${method}:${url}:${JSON.stringify(relevantHeaders)}:${body}`;
  }

  // MARK: isDuplicateRequest
  isDuplicateRequest(request) {
    const key = this.getRequestKey(request);
    return this.queue.some((item) => this.getRequestKey(item.request) === key);
  }

  // MARK: getRequestType
  // eslint-disable-next-line no-unused-vars
  getRequestType(request) {
    return "to_implement_request_type";
    // return request.url.split("/").pop();
    // const eventName = request.options.headers["Event-Name"];
    // return eventName || request.url.split("/").pop();
  }

  // MARK: trackRequest
  trackRequest(request, startTime) {
    const requestType = this.getRequestType(request);
    const processingTime = Date.now() - startTime;

    // Update request type counts
    this.analytics.requestTypes.set(
      requestType,
      (this.analytics.requestTypes.get(requestType) || 0) + 1
    );

    // Track processing time
    this.analytics.processingTimes.push(processingTime);

    // Keep only last 100 processing times
    if (this.analytics.processingTimes.length > 100) {
      this.analytics.processingTimes.shift();
    }
  }

  // MARK: trackError
  trackError(error) {
    const errorType = error.name || "UnknownError";

    this.analytics.errors.set(errorType, (this.analytics.errors.get(errorType) || 0) + 1);
  }

  // MARK: updateRequestTypeStats
  updateRequestTypeStats(requestType, success, processingTime, error = null) {
    if (!this.analytics.requestTypeStats.has(requestType)) {
      this.analytics.requestTypeStats.set(requestType, {
        total: 0,
        successful: 0,
        failed: 0,
        totalProcessingTime: 0,
        avgProcessingTime: 0,
        errors: new Map(),
        lastError: null,
        lastSuccess: null,
      });
    }

    const stats = this.analytics.requestTypeStats.get(requestType);
    stats.total++;
    stats.totalProcessingTime += processingTime;
    stats.avgProcessingTime = stats.totalProcessingTime / stats.total;

    if (success) {
      stats.successful++;
      stats.lastSuccess = new Date();
    } else {
      stats.failed++;
      stats.lastError = new Date();
      if (error) {
        const errorType = error.name || "UnknownError";
        stats.errors.set(errorType, (stats.errors.get(errorType) || 0) + 1);
      }
    }
  }

  // MARK: updateHourlyStats
  updateHourlyStats(success, processingTime) {
    const hour = new Date().toISOString().slice(0, 13); // YYYY-MM-DDTHH
    if (!this.analytics.hourlyStats.has(hour)) {
      this.analytics.hourlyStats.set(hour, {
        total: 0,
        successful: 0,
        failed: 0,
        totalProcessingTime: 0,
        avgProcessingTime: 0,
      });
    }

    const stats = this.analytics.hourlyStats.get(hour);
    stats.total++;
    stats.totalProcessingTime += processingTime;
    stats.avgProcessingTime = stats.totalProcessingTime / stats.total;

    if (success) {
      stats.successful++;
    } else {
      stats.failed++;
    }

    // Keep only last 24 hours of stats
    const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().slice(0, 13);
    for (const [hourKey] of this.analytics.hourlyStats) {
      if (hourKey < cutoff) {
        this.analytics.hourlyStats.delete(hourKey);
      }
    }
  }

  // MARK: add
  async add(request) {
    if (doLog()) console.log("Adding request to queue:", request);

    // Check queue size limit
    if (this.queue.length >= this.maxQueueSize) {
      const error = new Error("Queue size limit reached");
      if (this.onRequestFailed) {
        this.onRequestFailed(request, error);
      }
      throw error;
    }

    // Check for duplicates
    if (this.isDuplicateRequest(request)) {
      if (doLog()) console.log("Duplicate request detected, skipping");
      return Promise.resolve();
    }

    // Add to queue regardless of online status
    return this.addToQueue(request);
  }

  // MARK: addToQueue
  async addToQueue(request) {
    return new Promise((resolve, reject) => {
      this.queue.push({
        request,
        retryCount: 0,
        resolve,
        reject,
        addedAt: new Date(),
        isOffline: !this.isOnline,
      });

      // Always try to process after adding to queue
      this.process();
    });
  }

  // MARK: process
  async process() {
    if (this.processing || this.queue.length === 0) return;

    this.processing = true;

    try {
      while (
        this.queue.length > 0 &&
        this.isOnline &&
        this.visibilityState !== "hidden"
      ) {
        const item = this.queue[0];
        const startTime = Date.now();

        // Check if request has expired
        const queueTime = Date.now() - item.addedAt;
        if (queueTime > this.maxQueueTime) {
          if (doLog()) console.log(`Request expired after ${queueTime}ms in queue`);
          this.analytics.failedRequests++;
          this.analytics.totalRequests++;
          this.trackError(new Error("Request expired in queue"));
          item.reject(new Error(`Request expired after ${queueTime}ms in queue`));
          this.queue.shift();
          continue;
        }

        try {
          const response = await this.executeRequest(item.request);

          // Track successful request
          this.analytics.successfulRequests++;
          this.analytics.totalRequests++;
          const processingTime = Date.now() - startTime;

          // If this was a retry, increment successfulRecoveries
          if (item.retryCount > 0) {
            this.analytics.successfulRecoveries++;
          }

          // Update analytics
          this.trackRequest(item.request, startTime);
          this.updateRequestTypeStats(
            this.getRequestType(item.request),
            true,
            processingTime
          );
          this.updateHourlyStats(true, processingTime);

          item.resolve(response);
          this.queue.shift();
        } catch (error) {
          if (item.retryCount < this.maxRetries) {
            item.retryCount++;
            this.analytics.retries++;
            this.analytics.recoveryAttempts++;

            const backoff = this.calculateBackoff(item.retryCount);
            await new Promise((resolve) => setTimeout(resolve, backoff));
            continue;
          }

          // Max retries reached
          if (doLog()) console.error("Max retries reached, giving up");

          const errorMsg = `Request failed after ${this.maxRetries} retries: ${error.message}`;
          postError(errorMsg, error);

          if (this.onRequestFailed) {
            this.onRequestFailed(item.request, error);
          }

          this.analytics.failedRequests++;
          this.analytics.totalRequests++;

          item.reject(error);
          this.queue.shift();
        }
      }
    } finally {
      this.processing = false;
    }
  }

  // MARK: cleanupOldRequests
  cleanupOldRequests() {
    const now = Date.now();
    // Remove expired requests from queue
    this.queue = this.queue.filter((item) => {
      const queueTime = now - item.addedAt;
      if (queueTime > this.maxQueueTime) {
        if (doLog())
          console.log(`Removing expired request after ${queueTime}ms in queue`);
        this.analytics.failedRequests++;
        this.analytics.totalRequests++;
        this.trackError(new Error("Request expired in queue"));
        item.reject(new Error(`Request expired after ${queueTime}ms in queue`));
        return false;
      }
      return true;
    });
  }

  // MARK: calculateTimeout
  calculateTimeout() {
    try {
      const { type, effectiveType, rtt, downlink } = this.networkInfo;

      // Start with base timeout for network type
      let timeout =
        this.timeoutConfig.networkTimeouts[type] ||
        this.timeoutConfig.networkTimeouts[effectiveType] ||
        this.timeoutConfig.networkTimeouts.unknown;

      // Add RTT-based time
      timeout += rtt * this.timeoutConfig.factors.rttMultiplier;

      // Adjust based on bandwidth
      const bandwidth = downlink;
      let bandwidthMultiplier = 1;

      if (bandwidth >= this.timeoutConfig.factors.bandwidthThresholds.high) {
        bandwidthMultiplier = this.timeoutConfig.factors.bandwidthMultipliers.high;
      } else if (bandwidth >= this.timeoutConfig.factors.bandwidthThresholds.medium) {
        bandwidthMultiplier = this.timeoutConfig.factors.bandwidthMultipliers.medium;
      } else {
        bandwidthMultiplier = this.timeoutConfig.factors.bandwidthMultipliers.low;
      }

      timeout *= bandwidthMultiplier;

      // Log timeout calculation for debugging
      if (doLog()) {
        console.log("Timeout calculation:", {
          networkType: type,
          effectiveType,
          rtt,
          bandwidth,
          baseTimeout: timeout / bandwidthMultiplier,
          finalTimeout: timeout,
        });
      }

      return timeout;
    } catch (error) {
      // Fallback to requestTimeout if calculation fails
      console.warn("Timeout calculation failed, using default:", error);
      return this.requestTimeout;
    }
  }

  async executeRequest(request) {
    if (this.circuitBreaker.state === "OPEN") {
      if (
        Date.now() - this.circuitBreaker.lastFailureTime >
        this.circuitBreaker.resetTimeout
      ) {
        this.circuitBreaker.state = "HALF_OPEN";
      } else {
        throw new Error("Circuit breaker is open");
      }
    }

    const controller = new AbortController();
    const timeout = this.calculateTimeout();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const { url, options } = request;

      // Add network quality headers
      const enhancedOptions = {
        ...options, // includes headers, body, etc.
        signal: controller.signal,
      };

      const response = await fetch(url, enhancedOptions);
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (this.circuitBreaker.state === "HALF_OPEN") {
        this.circuitBreaker.state = "CLOSED";
        this.circuitBreaker.failures = 0;
      }

      return response;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error.name === "AbortError") {
        this.analytics.timeouts++;
        // Enhanced timeout error with network details
        throw new Error(
          `Request timeout after ${timeout}ms ` +
            `(Network: ${this.networkInfo.type}, ` +
            `Effective: ${this.networkInfo.effectiveType}, ` +
            `RTT: ${this.networkInfo.rtt}ms, ` +
            `Bandwidth: ${this.networkInfo.downlink}Mbps)`
        );
      }

      this.circuitBreaker.failures++;
      this.circuitBreaker.lastFailureTime = Date.now();

      if (this.circuitBreaker.failures >= this.circuitBreaker.threshold) {
        this.circuitBreaker.state = "OPEN";
        this.analytics.circuitBreakerTrips++;
      }

      throw error;
    }
  }

  // MARK: getQueueLength
  getQueueLength() {
    return this.queue.length;
  }

  // MARK: clearQueue
  clearQueue() {
    if (doLog()) console.log("Clearing request queue");
    this.queue = [];
    this.processing = false;
    this.resetAnalytics();
  }

  // MARK: isProcessing
  isProcessing() {
    return this.processing;
  }

  // MARK: getQueueStats
  getQueueStats() {
    const avgProcessingTime =
      this.analytics.processingTimes.length > 0
        ? this.analytics.processingTimes.reduce((a, b) => a + b) /
          this.analytics.processingTimes.length
        : 0;

    // Calculate success rates by request type
    const requestTypeSuccessRates = {};
    for (const [type, stats] of this.analytics.requestTypeStats) {
      requestTypeSuccessRates[type] =
        stats.total > 0 ? (stats.successful / stats.total) * 100 : 0;
    }

    // Calculate hourly success rates
    const hourlySuccessRates = {};
    for (const [hour, stats] of this.analytics.hourlyStats) {
      hourlySuccessRates[hour] =
        stats.total > 0 ? (stats.successful / stats.total) * 100 : 0;
    }

    return {
      queueLength: this.queue.length,
      offlineRequests: this.queue.filter((item) => item.isOffline).length,
      isOnline: this.isOnline,
      isProcessing: this.processing,
      visibilityState: this.visibilityState,
      circuitBreaker: {
        state: this.circuitBreaker.state,
        failures: this.circuitBreaker.failures,
        lastFailureTime: this.circuitBreaker.lastFailureTime,
        trips: this.analytics.circuitBreakerTrips,
      },
      analytics: {
        totalRequests: this.analytics.totalRequests,
        successfulRequests: this.analytics.successfulRequests,
        failedRequests: this.analytics.failedRequests,
        retries: this.analytics.retries,
        timeouts: this.analytics.timeouts,
        successRate:
          this.analytics.totalRequests > 0
            ? (this.analytics.successfulRequests / this.analytics.totalRequests) * 100
            : 0,
        avgProcessingTime,
        requestTypes: Object.fromEntries(this.analytics.requestTypes),
        requestTypeStats: Object.fromEntries(this.analytics.requestTypeStats),
        requestTypeSuccessRates,
        hourlyStats: Object.fromEntries(this.analytics.hourlyStats),
        hourlySuccessRates,
        errors: Object.fromEntries(this.analytics.errors),
        recoveryStats: {
          attempts: this.analytics.recoveryAttempts,
          successful: this.analytics.successfulRecoveries,
          successRate:
            this.analytics.recoveryAttempts > 0
              ? (this.analytics.successfulRecoveries / this.analytics.recoveryAttempts) *
                100
              : 0,
        },
      },
    };
  }

  // MARK: updateNetworkInfo
  updateNetworkInfo() {
    if (navigator.connection) {
      this.networkInfo = {
        type: navigator.connection.type,
        effectiveType: navigator.connection.effectiveType,
        rtt: navigator.connection.rtt,
        downlink: navigator.connection.downlink,
      };
    }
  }

  // MARK: recalculateTimeouts
  recalculateTimeouts() {
    // Recalculate timeouts for all queued requests
    this.queue.forEach((item) => {
      item.timeout = this.calculateTimeout();
    });
  }

  // MARK: resetAnalytics
  resetAnalytics() {
    if (doLog()) console.log("Resetting analytics");
    this.analytics = {
      totalRequests: 0,
      successfulRequests: 0,
      failedRequests: 0,
      retries: 0,
      requestTypes: new Map(),
      processingTimes: [],
      errors: new Map(),
      timeouts: 0,
      circuitBreakerTrips: 0,
      requestTypeStats: new Map(),
      hourlyStats: new Map(),
      recoveryAttempts: 0,
      successfulRecoveries: 0,
    };

    // Clear persisted analytics
    try {
      localStorage.removeItem("taalia_queue_state");
    } catch (error) {
      console.error("Error clearing persisted analytics:", error);
    }
  }
}

// Singleton instance with browser-specific options
export const requestQueue = new RequestQueue({
  maxQueueSize: 100,
  requestTimeout: 30000, // 30 seconds
  onRequestFailed: (request, error) => {
    console.error("Request failed permanently:", request, error);
  },
});
